import React, { Children } from 'react';
import classNames from 'classnames';

import { Icon } from 'js/design_system/Icon';
import { trackClick, Options as TrackClickOptions } from 'js/utils/trackClick';

import 'app/assets/stylesheets/design_system/button.scss';

type BaseButtonProps = {
  icon?: string | React.ReactNode;
  iconRight?: string | React.ReactNode;
  fullWidth?: boolean;
  trackClick?: TrackClickOptions;
};

type XsButtonProps = BaseButtonProps & {
  size: 'xs';
  secondary?: never;
  inverse?: never;
};

type StandardButtonProps = BaseButtonProps & {
  size?: 'sm' | 'lg';
  secondary?: boolean;
  inverse?: boolean;
};

// Shared between this and ButtonLink
export type ButtonProps = XsButtonProps | StandardButtonProps;

type ButtonPropsPlusNative = ButtonProps &
  React.ComponentPropsWithoutRef<'button'>;

/**
 * A button for handling click events or submitting forms. Supports several variants for different contexts.
 *
 * Props:
 * - `size`: The size of the button - one of 'xs', 'sm', or 'lg' (default: 'lg')
 * - `secondary`: Button for secondary actions, usually paired with a primary. Not valid for 'xs' size
 *     buttons. (default: false)
 * - `inverse`: Inverse-color button for dark backgrounds - NOT the same as secondary and can be combined
 *     with it. Not valid for 'xs' size buttons. (default: false)
 * - `icon`: The name of a Material Symbols icon (string) or an arbitrary React component to render as
 *     the button's icon (left of the text)
 * - `iconRight`: The name of a Material Symbols icon (string) or an arbitrary React component to render
 *     as the button's icon (right of the text)
 * - `fullWidth`: Full width button that takes up the entire width of its container (default: false)
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Button } from 'js/design_system/Button';
 *
 * <Button onClick={clickHandler} secondary>
 *   Click me!
 * </Button>
 * ```
 *
 */
export const Button = React.forwardRef<
  HTMLButtonElement,
  ButtonPropsPlusNative
>(
  (
    {
      size = 'lg',
      secondary = false,
      inverse = false,
      icon,
      iconRight,
      fullWidth = false,
      className,
      children,
      trackClick: trackClickOptions,
      onClick,
      ...restProps
    },
    ref,
  ) => {
    const classes = classNames('ds2-5-button', className, {
      'ds2-5-button--small': size === 'sm',
      'ds2-5-button--extra-small': size === 'xs',
      'ds2-5-button--secondary': secondary,
      'ds2-5-button--inverse': inverse,
      'ds2-5-button--full-width': fullWidth,
      'ds2-5-button--icon-only': Children.count(children) === 0,
    });

    let iconView = null;
    if (icon) {
      iconView =
        typeof icon === 'string' ? (
          <Icon name={icon} size={size === 'xs' ? 16 : 20} />
        ) : (
          icon
        );
    }

    let iconRightView = null;
    if (iconRight) {
      iconRightView =
        typeof iconRight === 'string' ? (
          <Icon name={iconRight} size={size === 'xs' ? 16 : 20} />
        ) : (
          iconRight
        );
    }

    const handleClick = trackClickOptions
      ? (event: React.MouseEvent<HTMLButtonElement>) => {
          if (trackClickOptions) {
            trackClick(trackClickOptions);
          }
          return onClick?.(event);
        }
      : onClick;

    return (
      <button
        className={classes}
        {...restProps}
        onClick={handleClick}
        ref={ref}
      >
        {iconView ? iconView : null}
        {children}
        {iconRightView ? iconRightView : null}
      </button>
    );
  },
);
